import {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useMemo,
} from 'react';
import { type ContextInterface } from '@convertcom/js-sdk';

import convertSdk from '../sdk/convertSdkClient';

import { useCookies } from 'hooks';

import { useUserContext } from './UserContext';

import { PAGE } from '../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';
import { COOKIE } from '../helpers/constants';
import getConfig from 'next/config';
import { trackBucketing } from '../helpers/experiments/tracking/trackBucketing';
import { getCtaDistributionBucketedCookie } from '../helpers/experiments/ctaDistributionExperiment.constants';
import { type CtaDistributionVariation } from '../helpers/experiments/ctaDistributionExperiment';
import { getGalleryInteractionsBucketedCookie } from '../helpers/experiments/galleryInteractionsExperiment.constants';

type UseConvertContextParameters = { page: PAGE };

type ConvertProviderParameters = {
  children: ReactNode;
  ctaDistributionVariationKey?: CtaDistributionVariation;
  galleryInteractionsVariationKey?: string | null;
};

const ConvertContext = createContext<{
  sdkContext: ContextInterface | null | undefined;
  ctaDistributionVariationKey: CtaDistributionVariation;
  galleryInteractionsVariationKey: string | null;
} | null>(null);
/**
 * This function allows the Convert SDK context to be accessed anywhere within the application
 * in order to run A/B experiments that require code changes.
 *
 * This may include displaying different components and executing different functions
 * depending on variations within the experiment.
 */
const ConvertProvider = (params: ConvertProviderParameters) => {
  const {
    children,
    ctaDistributionVariationKey = null,
    galleryInteractionsVariationKey = null,
  } = params;

  const {
    publicRuntimeConfig: {
      CTA_DISTRIBUTION_EXPERIMENT_KEY,
      GALLERY_INTERACTION_EXPERIMENT_KEY,
    },
  } = getConfig();

  const { get } = useCookies({ name: COOKIE.DD_CLIENT_ID });

  const ddClientId = get();

  const [sdkContext, setSdkContext] = useState<
    ContextInterface | null | undefined
  >();
  const {
    consentOptions: { allowConvert },
  } = useUserContext();

  useEffect(() => {
    const initSdk = async (userId: string) => {
      try {
        await convertSdk.onReady();
        const context = convertSdk.createContext(userId);
        setSdkContext(context);
      } catch (error) {
        setSdkContext(null);
        logger.error({
          error,
          message: 'Error loading Convert SDK',
          tags: [PAGE.APP],
        });
      }
    };
    if (
      allowConvert &&
      typeof ddClientId === 'string' &&
      ddClientId.trim().length > 0
    ) {
      initSdk(ddClientId);
    } else {
      setSdkContext(null);
    }
  }, [allowConvert, ddClientId]);

  useEffect(() => {
    if (!ctaDistributionVariationKey) return;

    trackBucketing({
      cookieName: getCtaDistributionBucketedCookie(),
      event: 'experiment_bucketed',
      experimentKey: CTA_DISTRIBUTION_EXPERIMENT_KEY,
      variationKey: ctaDistributionVariationKey,
    });
  }, [ctaDistributionVariationKey, CTA_DISTRIBUTION_EXPERIMENT_KEY]);

  useEffect(() => {
    if (!galleryInteractionsVariationKey) return;

    trackBucketing({
      cookieName: getGalleryInteractionsBucketedCookie(),
      event: 'experiment_bucketed',
      experimentKey: GALLERY_INTERACTION_EXPERIMENT_KEY,
      variationKey: galleryInteractionsVariationKey,
    });
  }, [galleryInteractionsVariationKey, GALLERY_INTERACTION_EXPERIMENT_KEY]);

  const contextValue = useMemo(
    () => ({
      sdkContext,
      ctaDistributionVariationKey,
      galleryInteractionsVariationKey,
    }),
    [sdkContext, ctaDistributionVariationKey, galleryInteractionsVariationKey],
  );

  return (
    <ConvertContext.Provider value={contextValue}>
      {children}
    </ConvertContext.Provider>
  );
};

const useConvertContext = (params: UseConvertContextParameters) => {
  const { page } = params;
  const context = useContext(ConvertContext);
  if (!context) {
    const error = new Error(
      'useConvertContext must be used within an instance of ConvertProvider',
    );
    logger.error({
      error,
      message:
        'useConvertContext must be used within an instance of ConvertProvider',
      tags: [page],
    });
    throw error;
  }
  return context;
};

export { ConvertProvider, useConvertContext, ConvertContext };
